/**
 * Event-type policy — which event types track attendance / close rate, and the
 * rules that follow from that. Pure functions so the behavior is unit-tested
 * directly (see eventTypePolicy.test.ts) and the UI just consumes the verdicts.
 *
 * Operator decisions (2026-06, PDF testing guide):
 *  - CK17: attendance is tracked for Classes, Speaking Engagements, and
 *    Seminar/Workshop/GSW; an event of those types must have an attendance count
 *    before it can be marked Completed.
 *  - CK18: gun shows, tables, and networking events do NOT track attendance, so
 *    there is no close rate for them — don't show an attendance field or close
 *    rate that could be filled in wrong and skew reporting.
 *
 * Close rate (verified members ÷ attendance) only exists where attendance is
 * tracked, so the attendance-tracked set drives both behaviors.
 */

import { EventType } from "../types/event.types";

/**
 * Event types where the operator records physical attendance (and therefore a
 * close rate). Per CK17: Class, Speaking Engagement, Seminar/Workshop/GSW.
 * Everything else (Gun Show, Table, Networking Event, Conference, Show, …) does
 * not track attendance.
 */
export const ATTENDANCE_TRACKED_EVENT_TYPES: ReadonlyArray<string> = [
  EventType.CLASS,
  EventType.SPEAKING_ENGAGEMENT,
  EventType.SEMINAR,
];

/** True when the event type records physical attendance (CK17). */
export function eventTracksAttendance(type?: string | null): boolean {
  return !!type && ATTENDANCE_TRACKED_EVENT_TYPES.includes(type);
}

/**
 * True when a close rate is meaningful for the event type. Close rate needs an
 * attendance denominator, so it follows attendance tracking exactly (CK18:
 * no close rate for gun shows / tables / networking events).
 */
export function eventTracksCloseRate(type?: string | null): boolean {
  return eventTracksAttendance(type);
}

/**
 * True when a status transition to "completed" must be BLOCKED because the event
 * tracks attendance (CK17) but no attendance count has been entered yet. Returns
 * false for non-attendance-tracked types (they can complete without a count) and
 * for tracked types that already have a count (including 0, which is a valid
 * "nobody showed" entry).
 */
export function attendanceRequiredBeforeCompleted(
  type?: string | null,
  attendanceCount?: number | null,
): boolean {
  if (!eventTracksAttendance(type)) {
    return false;
  }
  return attendanceCount === null || attendanceCount === undefined;
}

/** Operator-facing message when a completion is blocked by CK17. */
export const ATTENDANCE_REQUIRED_MESSAGE =
  "Enter the attendance count on the Overview tab before marking this event completed.";

/** Operator-facing message when completing a future-dated event is blocked (SA-2). */
export const FUTURE_EVENT_COMPLETE_MESSAGE =
  "This event is in the future — it can't be marked completed yet. If the date is wrong, update the event date first.";

/** YYYY-MM-DD in local time for a Date. */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/**
 * The event's calendar date as YYYY-MM-DD. Accepts a date-only string
 * ("2026-06-20"), a full ISO datetime with offset ("2026-06-20T10:00:00-05:00" —
 * the leading 10 chars are the event-local date), or a Date. Returns null if
 * absent/unparseable so callers don't block on unknown dates.
 */
function eventCalendarDate(eventStart?: string | Date | null): string | null {
  if (!eventStart) {
    return null;
  }
  if (typeof eventStart === "string") {
    const m = eventStart.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) {
      return m[1];
    }
    const d = new Date(eventStart);
    return Number.isNaN(d.getTime()) ? null : toLocalYmd(d);
  }
  return Number.isNaN(eventStart.getTime()) ? null : toLocalYmd(eventStart);
}

/**
 * SA-2: true when the event's date is AFTER today (a future-dated event). Compared
 * at calendar-date granularity (an event later today is NOT "future"); correcting
 * the date to today/past clears the block. Unknown/TBD dates → not future (no block).
 */
export function isFutureEvent(eventStart?: string | Date | null): boolean {
  const eventDate = eventCalendarDate(eventStart);
  if (!eventDate) {
    return false;
  }
  return eventDate > toLocalYmd(new Date());
}

/**
 * Single source of truth for whether a status change should be blocked, returning
 * the operator message to show (or null when allowed). BOTH the Event Detail
 * status dropdown AND the Event edit form call this, so the CK17 rule can't be
 * bypassed through one path (the audit caught the edit form skipping it). Today
 * the only blocked transition is "completed" without an attendance count on an
 * attendance-tracked event; centralizing it means future rules apply to every
 * entry point at once.
 */
export function eventStatusChangeBlockReason(
  nextStatus?: string | null,
  type?: string | null,
  attendanceCount?: number | null,
  eventStart?: string | Date | null,
): string | null {
  if (nextStatus === "completed" && isFutureEvent(eventStart)) {
    // SA-2: can't complete an event that hasn't happened yet (prevents entering
    // data on the wrong, future-dated event). Checked before the attendance gate
    // since a future event shouldn't have attendance anyway.
    return FUTURE_EVENT_COMPLETE_MESSAGE;
  }
  if (
    nextStatus === "completed" &&
    attendanceRequiredBeforeCompleted(type, attendanceCount)
  ) {
    return ATTENDANCE_REQUIRED_MESSAGE;
  }
  return null;
}

/**
 * MSR slice 4 (per-day close, owner decisions 2026-10-08 / 10-11) — mirrors the server functions
 * msr_day_attendance_source / msr_day_has_close_rate (migration 2026-10-11-500), which are authoritative:
 *  - manual entry ONLY for class, seminar (Seminar/Workshop/GSW) and speaking_engagement (same set as CK17);
 *  - range nights and gun shows (gun_show, legacy show): attendance = that day's check-ins (registered + walk-in);
 *  - other types: no attendance.
 * Close rate = verified ÷ attendance for the manual types and range nights; gun shows have none.
 */
export type DayAttendanceSource = "manual" | "checkins" | "none";

export function eventDayAttendanceSource(
  type?: string | null,
): DayAttendanceSource {
  if (eventTracksAttendance(type)) {
    return "manual";
  }
  if (type === "range_night" || type === "gun_show" || type === "show") {
    return "checkins";
  }
  return "none";
}

export function eventDayHasCloseRate(type?: string | null): boolean {
  return eventTracksAttendance(type) || type === "range_night";
}

/**
 * Owner decision 2026-10-29 ("no-promo" types): a class, table, seminar ("Seminar / Workshop / GSW") or speaking
 * engagement sends NO emails (no Event in a Box invitation or reminder, no audience list, no campaign), has NO
 * registration / landing page, gets NO automatic flyer and NO event CTA / QR. Its Calls to Action tab shows the
 * venue's and host organization's CTAs instead (read-only), and signups from those count toward the event when they
 * match its place and time. The server enforces all of it (migration 2026-10-29-1260, event_type_is_no_promo, which
 * holds the same list -- keep the two in step).
 */
export const NO_PROMO_EVENT_TYPES: ReadonlyArray<string> = [
  EventType.CLASS,
  EventType.TABLE,
  EventType.SEMINAR,
  EventType.SPEAKING_ENGAGEMENT,
];

/** True for class, table, seminar and speaking_engagement (see NO_PROMO_EVENT_TYPES). */
export function eventTypeIsNoPromo(type?: string | null): boolean {
  return NO_PROMO_EVENT_TYPES.includes((type ?? "").trim().toLowerCase());
}

/**
 * CFG-04 (owner decision 2026-10-12, supersedes the class-only rule of the same day): ONLY gun shows and range nights
 * need setup. Seminars ("Seminar / Workshop / GSW") needed it until 2026-10-29: their setup was the Event in a Box
 * invitation, which they no longer have (NO_PROMO_EVENT_TYPES), so a seminar now has no setup step either.
 * Every other type in the events_type_check list -- class, table, speaking_engagement, conference, show, other,
 * networking_event, member_check_in, lightning_deals, private_event, community_event, meeting, training, webinar,
 * call, social -- has no setup step and is never saved or shown as setup-incomplete. The server enforces the same
 * rule (migration 2026-10-12-510, trigger trg_zzz_events_class_never_setup_incomplete; the name is historic).
 * An allow-list on purpose: a type added later skips setup until the owner says it needs one.
 */
export const SETUP_EVENT_TYPES: ReadonlyArray<string> = [
  EventType.GUN_SHOW,
  EventType.RANGE_NIGHT,
];

/** True only for gun_show and range_night: the types that have a setup step. */
export function eventTypeNeedsSetup(type?: string | null): boolean {
  return SETUP_EVENT_TYPES.includes((type ?? "").trim().toLowerCase());
}

/** Should the event show as "Incomplete" (badge, Mark as set up)? Only when flagged AND its type has a setup step. */
export function showsSetupIncomplete(event: {
  setup_incomplete?: boolean | null;
  type?: string | null;
}): boolean {
  // The database stores setup_incomplete only on a setup type, or (mig 1241) on an event a Regional / Territory
  // Manager created, which waits for its QR to be published by the events team: the column is the answer.
  return event.setup_incomplete === true;
}

/** RUN-02: what the rep checks into, in the start question ("Check into your gun show."). */
export function eventRunNoun(type?: string | null): string {
  switch ((type ?? "").trim().toLowerCase()) {
    case EventType.CLASS:
      return "class";
    case EventType.GUN_SHOW:
    case EventType.SHOW:
      return "gun show";
    case EventType.RANGE_NIGHT:
      return "range night";
    case EventType.SEMINAR:
      return "seminar";
    case EventType.TABLE:
      return "table";
    case EventType.SPEAKING_ENGAGEMENT:
      return "speaking engagement";
    default:
      return "event";
  }
}

/**
 * RUN-02: the items listed under the start question (the three the server records). A class / table / seminar /
 * speaking engagement has no event QR code (2026-10-29), so its third item is the venue's QR sign.
 */
export function startDayItems(noun: string, type?: string | null): string[] {
  return [
    "I'm at the venue",
    `My table / booth for the ${noun} is set up`,
    eventTypeIsNoPromo(type)
      ? "The venue's QR sign is displayed and I've confirmed it works"
      : "The QR sign is displayed and I've confirmed it works",
  ];
}

export function startDayQuestion(noun: string): string {
  return `Check into your ${noun}. Are these items complete and ready?`;
}

/**
 * Event types that no longer get an event CTA or QR code (owner 2026-10-29). An RM/TM-created event of one of these
 * types is never held "incomplete" waiting for a QR, and the "needs configuration" notice never asks for one
 * (server side: migration 2026-10-29-1246).
 */
export const NO_CTA_QR_EVENT_TYPES: ReadonlyArray<string> = [
  EventType.CLASS,
  EventType.TABLE,
  EventType.SEMINAR,
  EventType.SPEAKING_ENGAGEMENT,
];

/** True when the event type has no event CTA / QR (so there is no QR to wait for). */
export function eventTypeHasNoCtaQr(type?: string | null): boolean {
  // the same four types as the no-promo branch's NO_PROMO_EVENT_TYPES (one rule)
  return !!type && eventTypeIsNoPromo(type);
}
